<template>
    <component :is="page ? 'div' : 'a-modal'" v-bind="page ? {} : modalProps" @cancel="$emit('close')">
        <div class="pbm-modal" :class="{ 'pbm-page': page }">
            <div class="pbm-head" v-if="!page">
                <div>
                    <h3>Marcas</h3>
                    <div class="pbm-sub">{{ product.pro_descricao }}</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="pbm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="pbm-tools">
                <a-input v-model="search" class="pbm-search" placeholder="Buscar por marca" allow-clear>
                    <a-icon slot="prefix" type="search" />
                </a-input>
                <a-checkbox v-model="onlyActive">Apenas produtos ativos</a-checkbox>
                <a-input-search
                    v-if="can('PRODUCT', 'UPDATE')"
                    v-model="newBrand"
                    class="pbm-search pbm-tools-right"
                    placeholder="Nova marca"
                    :loading="saving"
                    @search="addBrand"
                >
                    <a-button slot="enterButton" type="primary" icon="plus" />
                </a-input-search>
            </div>

            <div class="pbm-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="filteredBrands"
                    row-key="pro_id"
                    :loading="loading"
                    :pagination="{ pageSize: 20, size: 'small' }"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Nenhuma marca encontrada.' }"
                >
                    <template slot="brand" slot-scope="text">{{ text || "--" }}</template>
                    <template slot="code" slot-scope="text, brand">
                        <a-input
                            v-if="editingCodeId === brand.pro_id"
                            v-model="editingCode"
                            size="small"
                            class="pbm-code-input"
                            placeholder="Código interno"
                            @input="sanitizeCode"
                            @pressEnter="saveCode(brand)"
                        />
                        <span v-else>{{ text || "--" }}</span>
                    </template>
                    <template slot="preferred" slot-scope="text, brand">
                        <a-tooltip :title="brand.pro_preferencial == 1 ? 'Marca preferencial' : 'Definir como preferencial'">
                            <i
                                class="fa fa-star pbm-star"
                                :class="{ 'pbm-star--active': brand.pro_preferencial == 1 }"
                                @click="togglePreferred(brand)"
                            ></i>
                        </a-tooltip>
                    </template>
                    <template slot="money" slot-scope="text">
                        <span class="pbm-money">{{ text | currency }}</span>
                    </template>
                    <template slot="status" slot-scope="text, brand">
                        <a-tag :color="brand.pro_status == '1' ? 'green' : 'default'">
                            {{ brand.pro_status == "1" ? "Ativo" : "Inativo" }}
                        </a-tag>
                    </template>
                    <template slot="acoes" slot-scope="text, brand">
                        <ActionMenu :actions="getActions(brand)" />
                    </template>
                </a-table>
            </div>

            <div class="pbm-foot" v-if="!page">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>

        <cc-loader-full v-if="loadingFull" />
        <ProductLogV2Modal v-if="logBrand" :product="logBrand" @close="logBrand = null" />
    </component>
</template>

<script>
import * as CONSTANTS from "@/constants/constants";
import ProductService from "@/services/ProductService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { perm_mixin } from "@/mixins/permission.mixin";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import ProductLogV2Modal from "./product-log-v2.modal.vue";

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default {
    name: "ProductBrandsV2Modal",
    mixins: [perm_mixin],
    components: { ActionMenu, ProductLogV2Modal },
    props: {
        product: { type: Object, required: true },
        page: { type: Boolean, default: false },
    },
    data() {
        return {
            prod_svc: new ProductService(),
            loading: false,
            loadingFull: false,
            saving: false,
            brands: [],
            search: "",
            onlyActive: true,
            newBrand: "",
            editingCodeId: null,
            editingCode: "",
            pinning: false,
            logBrand: null,
            columns: [
                { title: "Marca", dataIndex: "pro_observacao", key: "pro_observacao", scopedSlots: { customRender: "brand" } },
                { title: "Cód. interno", dataIndex: "pro_codInterno", key: "pro_codInterno", scopedSlots: { customRender: "code" } },
                { title: "Preferencial", key: "preferred", align: "center", scopedSlots: { customRender: "preferred" } },
                { title: "Valor Últ. Compra", dataIndex: "pro_ultimaCompra", key: "pro_ultimaCompra", align: "right", scopedSlots: { customRender: "money" } },
                { title: "Status", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Ações", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    computed: {
        modalProps() {
            return {
                visible: true,
                width: "65vw",
                footer: null,
                closable: false,
                destroyOnClose: true,
                centered: true,
                wrapClassName: "pbm-wrap",
            };
        },
        filteredBrands() {
            const term = (this.search || "").trim().toLowerCase();
            return this.brands
                .filter((b) => !this.onlyActive || b.pro_status == "1")
                .filter((b) => !term || String(b.pro_observacao || "").toLowerCase().includes(term));
        },
    },
    created() {
        this.loadBrands();
    },
    methods: {
        loadBrands() {
            this.loading = true;
            return this.prod_svc
                .get_product_brands(this.product.pro_id)
                .then(({ data }) => {
                    this.brands = (data.produtos || [])
                        .slice()
                        .sort((a, b) => String(a.pro_observacao || "").localeCompare(String(b.pro_observacao || "")));
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getActions(brand) {
            const canUpdate = this.can("PRODUCT", "UPDATE");
            const editing = this.editingCodeId === brand.pro_id;
            return [
                {
                    label: brand.pro_status == "1" ? "Inativar marca" : "Ativar marca",
                    icon: brand.pro_status == "1" ? "block" : "verified",
                    iconClass: brand.pro_status == "1" ? "action-danger" : "action-success",
                    visible: canUpdate,
                    onClick: () => this.toggleStatus(brand),
                },
                editing
                    ? { label: "Salvar código interno", icon: "save", iconClass: "action-success", visible: canUpdate, onClick: () => this.saveCode(brand) }
                    : { label: "Editar código interno", icon: "edit", iconClass: "action-info", visible: canUpdate, onClick: () => this.startEditingCode(brand) },
                { label: "Histórico de alterações", icon: "format_list_bulleted", iconClass: "action-primary", onClick: () => (this.logBrand = brand) },
            ];
        },
        addBrand() {
            const marca = (this.newBrand || "").trim();
            if (!marca) return;
            this.saving = true;
            this.prod_svc
                .save_brand({ produto: this.product.pro_id, marca })
                .then(() => {
                    this.newBrand = "";
                    this.$swal.fire("Sucesso!", "Nova marca adicionada com sucesso.", "success");
                    return this.loadBrands();
                })
                .catch(() => {
                    this.$store.dispatch("notification/add", { type: "error", message: "Não foi possível salvar a marca!" });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        togglePreferred(brand) {
            if (this.pinning) return;
            this.pinning = true;
            const previous = brand.pro_preferencial;
            brand.pro_preferencial = previous == 1 ? 0 : 1;
            this.prod_svc
                .pin_brand({ idProduto: brand.pro_id, preferencial: brand.pro_preferencial })
                .catch((error) => {
                    brand.pro_preferencial = previous;
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    setTimeout(() => (this.pinning = false), 500);
                });
        },
        toggleStatus(brand) {
            this.loadingFull = true;
            axios
                .put(
                    `${CONSTANTS.API_URL}/atualizarprodutonormalcliente/ativar`,
                    { pro_id: brand.pro_id, pro_status: brand.pro_status == "1" ? "0" : "1" },
                    authConfig()
                )
                .then(({ data }) => {
                    brand.pro_status = data.pro_status;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loadingFull = false;
                });
        },
        startEditingCode(brand) {
            this.editingCodeId = brand.pro_id;
            this.editingCode = brand.pro_codInterno ? String(brand.pro_codInterno) : "";
        },
        sanitizeCode(event) {
            const value = String(event.target.value || "").replace(/\D/g, "");
            this.editingCode = value;
            event.target.value = value;
        },
        saveCode(brand) {
            this.loadingFull = true;
            axios
                .put(`${CONSTANTS.API_V3_URL}/clients/products/${brand.pro_id}`, { pro_codInterno: this.editingCode }, authConfig())
                .then(() => {
                    this.editingCodeId = null;
                    return this.loadBrands();
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.mensagem;
                    this.$swal.fire("Desculpe.", message || "Não foi possível salvar o código interno.", "error");
                })
                .finally(() => {
                    this.loadingFull = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.pbm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.pbm-page {
        max-height: none;
        overflow: visible;

        .pbm-tools {
            padding-left: 0;
            padding-right: 0;
        }

        .pbm-body {
            overflow: visible;
            padding: 0;
            border-top: none;
        }
    }
}
.pbm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.pbm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.pbm-x.ant-btn,
.pbm-x.ant-btn:focus,
.pbm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.pbm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.pbm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.pbm-search {
    flex: 1 1 220px;
    max-width: 320px;
}
.pbm-tools-right {
    margin-left: auto;
}
.pbm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.pbm-code-input {
    width: 140px;
}
.pbm-star {
    cursor: pointer;
    font-size: 1.1rem;
    color: #d9d9d9;
}
.pbm-star--active {
    color: #fadb14;
}
.pbm-money {
    white-space: nowrap;
}
.pbm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.pbm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
